import { useContext, useMemo } from 'react'

import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { useOfferPageFlightPricePerPerson } from 'hooks/useFlightPrice'
import { sortBy } from 'lib/array/arrayUtils'

export type FlightToggleDisplayPrice =
  | { amount: number; unit: 'person'; source: 'calculated' }
  | { amount: number; unit: 'adult'; source: 'tacticalDeal'; deal: Pick<App.FlightDeal, 'perAdultPrice' | 'luxPlusPerAdultPrice'> }
  | { amount: number; unit: 'person'; source: 'legacyTotal' }

interface Options {
  airportCode?: string
  disabled?: boolean
}

function useOfferPageFlightToggleDisplayPrice({ airportCode, disabled }: Options): FlightToggleDisplayPrice | undefined {
  const offerPageState = useContext(OfferPageStateContext)
  const flightDealFilters = useMemo(() => ({
    originAirportCode: airportCode,
    destinationAirportCode: offerPageState.offer?.flightDestinationPort,
  }), [airportCode, offerPageState.offer?.flightDestinationPort])
  const [deals, dealsLoading] = useFlightDeals('all', flightDealFilters, { disabled })
  const cheapestDeal = useMemo(() => sortBy(deals, deal => deal.perAdultPrice, 'asc')[0], [deals])
  const { flightPricePerPerson, loading: flightPriceLoading } = useOfferPageFlightPricePerPerson({
    disabled: disabled || deals.length > 0,
  })

  if (disabled) {
    return undefined
  }

  if (dealsLoading) {
    return undefined
  }

  if (cheapestDeal) {
    return {
      amount: cheapestDeal.perAdultPrice,
      unit: 'adult',
      source: 'tacticalDeal',
      deal: cheapestDeal,
    }
  }

  if (flightPriceLoading) {
    return undefined
  }

  if (flightPricePerPerson) {
    return {
      amount: flightPricePerPerson,
      unit: 'person',
      source: 'calculated',
    }
  }
}

export default useOfferPageFlightToggleDisplayPrice
